import { useEffect, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  FONT,
  HEADING_FG,
  MUTED_FG,
  POSITIVE_BG,
  POSITIVE_FG,
  RANK_OTHER_FG,
  SURFACE,
  WARN_BG,
  WARN_FG,
  WHITE,
  eyebrowSx,
  sectionSubtitleSx,
  tableSx,
} from '../reportTokens';
import type { ReportMapHistory } from '../types/report.types';

/** Shown inside a section that has nothing to say this week. */
export function EmptyNote({ children }: { children: React.ReactNode }) {
  return <Typography sx={sectionSubtitleSx}>{children}</Typography>;
}

/** Colour a rank the way the design does: podium, page one, the rest. */
const rankStyle = (rank: number | null) => {
  if (rank === null) return { color: MUTED_FG, bgcolor: 'transparent' };
  if (rank <= 3) return { color: POSITIVE_FG, bgcolor: POSITIVE_BG };
  if (rank <= 10) return { color: WARN_FG, bgcolor: WARN_BG };
  return { color: RANK_OTHER_FG, bgcolor: SURFACE };
};

const weekLabel = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/** The first column, pinned left on the card's white so weeks pass under it. */
const STICKY_KEYWORD = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: WHITE,
} as const;

/**
 * Keywords down the side, weeks across the top — the design's rank grid.
 *
 * Wider than a phone once there are a few weeks, so it scrolls sideways — and
 * opens on the newest weeks at the right, the ones the reader came for, rather
 * than on the oldest. The keyword column stays put while the weeks scroll
 * under it, or opening at the right end would cut the keywords off.
 */
export function RankGrid({ history }: { history: ReportMapHistory }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const toNewest = () => {
      const scroller = scrollerRef.current;
      if (scroller) scroller.scrollLeft = scroller.scrollWidth;
    };
    toNewest();
    // Again once the web font lands: it widens the headers, and the first
    // scroll would stop short of the newest column.
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) toNewest();
    });
    return () => {
      cancelled = true;
    };
  }, [history]);

  if (!history?.weeks?.length || !history.rows.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ ...eyebrowSx, mb: 1 }}>Rank by week</Typography>
      <TableContainer
        ref={scrollerRef}
        // Printed whole: a scroll box prints at its scroll position, which
        // (opened on the newest weeks) would cut the older ones from the PDF.
        sx={{ overflowX: 'auto', '@media print': { overflowX: 'visible' } }}
      >
        <Table sx={{ ...tableSx, minWidth: 420 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={STICKY_KEYWORD}>Keyword</TableCell>
              {history.weeks.map(week => (
                <TableCell key={week} align='center'>
                  {weekLabel(week)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {history.rows.map(row => (
              <TableRow key={row.keyword}>
                <TableCell
                  sx={{ ...STICKY_KEYWORD, fontWeight: 600, color: HEADING_FG }}
                >
                  {row.keyword}
                </TableCell>
                {row.ranks.map((rank, index) => {
                  const style = rankStyle(rank);
                  return (
                    <TableCell key={history.weeks[index]} align='center'>
                      <Box
                        component='span'
                        sx={{
                          display: 'inline-block',
                          minWidth: 30,
                          px: 0.75,
                          py: 0.3,
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: FONT.fine,
                          ...style,
                        }}
                      >
                        {/* Not ranked is a real result and says so, rather
                            than borrowing a number it never held. */}
                        {rank === null ? '—' : rank}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
